import type React from "react";
import { useTranslation } from "react-i18next";
import type { StatsNode } from "../../../types/graphStatsTypes";

interface StatsTooltipProps {
  node: StatsNode | null;
  x: number;
  y: number;
  containerRect?: DOMRect | null;
}

const StatsTooltip: React.FC<StatsTooltipProps> = ({ node, x, y, containerRect }) => {
  const { t } = useTranslation();

  if (!node) return null;

  // Calculate tooltip position to keep it within bounds
  const tooltipWidth = 220;
  const tooltipHeight = 120;

  let tooltipX = x + 15;
  let tooltipY = y + 15;

  if (containerRect) {
    // Adjust if tooltip would overflow right edge
    if (tooltipX + tooltipWidth > containerRect.width) {
      tooltipX = x - tooltipWidth - 15;
    }
    // Adjust if tooltip would overflow bottom edge
    if (tooltipY + tooltipHeight > containerRect.height) {
      tooltipY = y - tooltipHeight - 15;
    }
  }

  const formatDuration = (seconds: number): string => {
    if (seconds < 60) return `${Math.round(seconds)}s`;
    const mins = Math.floor(seconds / 60);
    const secs = Math.round(seconds % 60);
    return `${mins}m ${secs}s`;
  };

  const getSuccessRateTextColor = (rate: number): string => {
    if (rate >= 80) return "text-green-600";
    if (rate >= 50) return "text-yellow-600";
    return "text-red-600";
  };

  return (
    <div
      className="absolute z-50 pointer-events-none"
      style={{
        left: tooltipX,
        top: tooltipY,
      }}
    >
      <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-3 min-w-[200px]">
        {/* Header */}
        <div className="mb-2 pb-2 border-b border-gray-100">
          <p className="font-semibold text-gray-900 text-sm truncate max-w-[180px]">{node.name}</p>
          <p className="text-xs text-gray-500">
            {node.type === "scene" ? t("statsNew.graph.scene") : t("statsNew.graph.interaction")}
            {node.type === "scene" && node.sceneType && ` (${node.sceneType})`}
          </p>
        </div>

        {/* Stats */}
        {node.stats ? (
          <div className="space-y-1.5 text-sm">
            {/* Module view: show success rate + attempts */}
            {/* Session view: show score instead */}
            {node.visited === undefined ? (
              <>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">{t("statsNew.graph.successRate")}:</span>
                  <span
                    className={`font-medium ${getSuccessRateTextColor(node.stats.successRate)}`}
                  >
                    {Math.round(node.stats.successRate)}%
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">{t("statsNew.graph.attempts")}:</span>
                  <span className="font-medium text-gray-900">{node.stats.attemptCount}</span>
                </div>
              </>
            ) : node.stats.totalScore != null && node.stats.totalScoreMax != null ? (
              <div className="flex justify-between items-center">
                <span className="text-gray-600">{t("statsNew.graph.score")}:</span>
                <span className={`font-medium ${getSuccessRateTextColor(node.stats.successRate)}`}>
                  {node.stats.totalScore}/{node.stats.totalScoreMax}
                </span>
              </div>
            ) : null}
            <div className="flex justify-between items-center">
              <span className="text-gray-600">{t("statsNew.graph.avgDuration")}:</span>
              <span className="font-medium text-gray-900">
                {formatDuration(node.stats.avgDuration)}
              </span>
            </div>
          </div>
        ) : node.visited === false ? (
          <p className="text-sm text-gray-400 italic">{t("statsNew.graph.notVisited")}</p>
        ) : (
          <p className="text-sm text-gray-400 italic">{t("statsNew.graph.noData")}</p>
        )}

        {/* Visited indicator for session view */}
        {node.visited !== undefined && (
          <div className="mt-2 pt-2 border-t border-gray-100">
            <span
              className={`text-xs px-2 py-0.5 rounded-full ${
                node.visited ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"
              }`}
            >
              {node.visited ? t("statsNew.graph.visited") : t("statsNew.graph.notVisited")}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default StatsTooltip;
